import { api } from './api';

export type Participant = { name: string; role?: string };

export type MeetingListItem = {
  id: string;
  title: string;
  status: 'draft' | 'live' | 'completed' | 'archived';
  primary_language: string;
  started_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  audio_path: string | null;
  video_path: string | null;
  has_summary: number;
  created_at: string;
  updated_at: string;
  participants_count: number;
  brief_len: number;
};

export type MeetingDetail = {
  id: string;
  user_id: number;
  title: string;
  status: MeetingListItem['status'];
  brief_md: string | null;
  participants: Participant[] | null;
  primary_language: string;
  started_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  audio_path: string | null;
  video_path: string | null;
  thumbnail_path: string | null;
  summary_md: string | null;
  action_items: unknown;
  created_at: string;
  updated_at: string;
};

export const meetingsApi = {
  list: (status?: MeetingListItem['status']) =>
    api.get<MeetingListItem[]>(
      '/api/meetings' + (status ? `?status=${status}` : ''),
    ),
  get: (id: string) => api.get<MeetingDetail>(`/api/meetings/${id}`),
  create: (data: {
    title: string;
    brief_md?: string;
    participants?: Participant[];
    primary_language?: string;
  }) => api.post<{ id: string }>('/api/meetings', data),
  update: (
    id: string,
    patch: Partial<{
      title: string;
      brief_md: string;
      participants: Participant[];
      primary_language: string;
      status: MeetingListItem['status'];
    }>,
  ) => api.patch<{ ok: boolean }>(`/api/meetings/${id}`, patch),
  remove: (id: string) => api.del<{ ok: boolean }>(`/api/meetings/${id}`),
};

export async function transcribeBlob(blob: Blob, lang: string = 'ru'): Promise<string> {
  const fd = new FormData();
  const ext = blob.type.includes('webm') ? 'webm' : blob.type.includes('mp4') ? 'm4a' : 'audio';
  fd.append('audio', blob, `dictation.${ext}`);
  const res = await fetch(`/api/transcribe?lang=${encodeURIComponent(lang)}`, {
    method: 'POST',
    credentials: 'include',
    body: fd,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || 'Transcribe failed');
  }
  const data = (await res.json()) as { text: string };
  return data.text;
}
